import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isApiError } from "@/api/errors";
import {
  confirmAllMatching,
  getMatching,
  isMatchingUnavailable,
  rebuildMatching,
  resolveMatching,
  skipMatching,
} from "@/api/matching";
import type {
  ClientSummary,
  EmployeeSummary,
  MatchingCandidate,
  MatchingCandidateClient,
  MatchingCandidateSpeaker,
  MatchingResponse,
  MatchingSuggestion,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { ClientFormDialog } from "@/components/registries/ClientFormDialog";
import { EmployeeFormDialog } from "@/components/registries/EmployeeFormDialog";
import { useCan } from "@/hooks/useCan";

export interface MatchingTabProps {
  meetingId: UUID;
}

/**
 * Phase 6 — Matching tab.
 *
 * Loads `GET /api/meetings/:id/matching` and renders the client banner
 * plus the speaker rows. Each row exposes the same set of actions:
 *
 *   • Link (pick a top-N suggestion)             → POST /matching/:id/resolve
 *   • Create new employee/client (full form)     → opens EmployeeFormDialog
 *                                                   then auto-binds onSaved
 *   • Skip                                       → POST /matching/:id/skip
 *   • Change (re-open suggestions for a row that
 *     is already bound; same wire format as Link)
 *
 * Backend not yet deployed → 404 surfaces as the "unavailable" empty state
 * so the rest of the card keeps working.
 */
export function MatchingTab({ meetingId }: MatchingTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const canRebuild = useCan("matching_rebuild");

  const query = useQuery<MatchingResponse>({
    queryKey: ["matching", meetingId],
    queryFn: () => getMatching(meetingId),
    retry: (_count, err) => !isMatchingUnavailable(err),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["matching", meetingId] });

  // We keep the *which candidate failed* alongside the mutation state
  // so the UI can highlight the right row, not just dump a generic
  // banner at the bottom of the tab. Before this, a 5xx silently
  // looked like the button «did nothing» (TaskMe 8760a3e1).
  const [lastResolveFailedFor, setLastResolveFailedFor] = useState<UUID | null>(
    null,
  );
  const resolve = useMutation({
    mutationFn: ({
      candidateId,
      entityId,
    }: {
      candidateId: UUID;
      entityId: UUID;
    }) =>
      resolveMatching(candidateId, { entity_id: entityId, confirmed: true }),
    onMutate: () => setLastResolveFailedFor(null),
    onSuccess: () => {
      setLastResolveFailedFor(null);
      invalidate();
    },
    onError: (_err, variables) =>
      setLastResolveFailedFor(variables.candidateId),
  });
  const skip = useMutation({
    mutationFn: (candidateId: UUID) => skipMatching(candidateId),
    onSuccess: invalidate,
  });
  const rebuild = useMutation({
    mutationFn: () => rebuildMatching(meetingId),
    onSuccess: invalidate,
  });
  const confirmAll = useMutation({
    mutationFn: () => confirmAllMatching(meetingId),
    onSuccess: invalidate,
  });

  // `createDialog` switches between "create new employee for candidate X"
  // and "create new client for candidate X". `null` means no dialog open.
  const [createDialog, setCreateDialog] = useState<
    | { entityType: "employee"; candidateId: UUID }
    | { entityType: "client"; candidateId: UUID }
    | null
  >(null);
  // "Change" expands the suggestions of an already-bound row.
  const [expandedRowId, setExpandedRowId] = useState<UUID | null>(null);

  if (query.isLoading) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="matching-loading"
      >
        {t("meetings.card.matching.loading")}
      </p>
    );
  }
  if (query.isError) {
    if (isMatchingUnavailable(query.error)) {
      return (
        <p
          className="text-sm text-muted-foreground"
          data-testid="matching-unavailable"
        >
          {t("meetings.card.matching.unavailable")}
        </p>
      );
    }
    return (
      <p className="text-sm text-destructive" role="alert">
        {t("meetings.card.matching.loadError")}
      </p>
    );
  }
  const data = query.data;
  if (data === undefined) return null;

  function handleLink(candidateId: UUID, entityId: UUID): void {
    setExpandedRowId(null);
    resolve.mutate({ candidateId, entityId });
  }
  function handleSkip(candidateId: UUID): void {
    skip.mutate(candidateId);
  }
  function handleRebuild(): void {
    if (window.confirm(t("meetings.card.matching.rebuildConfirm"))) {
      rebuild.mutate();
    }
  }
  function handleCreatedEmployee(candidateId: UUID, e: EmployeeSummary): void {
    setCreateDialog(null);
    resolve.mutate({ candidateId, entityId: e.id });
  }
  function handleCreatedClient(candidateId: UUID, c: ClientSummary): void {
    setCreateDialog(null);
    resolve.mutate({ candidateId, entityId: c.id });
  }

  return (
    <div className="space-y-6" data-testid="matching-tab">
      <MatchingToolbar
        pendingCount={data.pending_count}
        canRebuild={canRebuild}
        rebuildBusy={rebuild.isPending}
        confirmAllBusy={confirmAll.isPending}
        onRebuild={handleRebuild}
        onConfirmAll={() => confirmAll.mutate()}
      />
      {/* Sticky top banner so a failed «Привязать» is impossible to
          miss — the previous footer-only error message was easy to
          overlook below the fold and the operator reported the
          button as «doing nothing» (TaskMe 8760a3e1). */}
      {resolve.isError ? (
        <p
          className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
          data-testid="matching-resolve-error"
        >
          {resolveErrorMessage(t, resolve.error)}
        </p>
      ) : null}
      {/* TaskMe 8760a3e1 (revisit) — Dmitri simplified: one client per
          meeting, set on upload and edited manually from the card. The
          «Клиент из диалога» comparison block and the «Расхождение»
          banner are gone; the form-pick is the sole, full-width client
          tile. Operator changes the client via the «Изменить клиента»
          button on the card header (``MeetingClientBlock``). */}
      <ClientBlock
        candidate={data.client}
        expanded={data.client !== null && expandedRowId === data.client.id}
        failedResolveId={lastResolveFailedFor}
        onToggleExpanded={(id) =>
          setExpandedRowId(expandedRowId === id ? null : id)
        }
        onLink={handleLink}
        onSkip={handleSkip}
        onCreateNew={(candidateId) =>
          setCreateDialog({ entityType: "client", candidateId })
        }
      />
      <SpeakersBlock
        speakers={data.speakers}
        expandedRowId={expandedRowId}
        failedResolveId={lastResolveFailedFor}
        onToggleExpanded={(id) =>
          setExpandedRowId(expandedRowId === id ? null : id)
        }
        onLink={handleLink}
        onSkip={handleSkip}
        onCreateNew={(candidateId) =>
          setCreateDialog({ entityType: "employee", candidateId })
        }
      />
      {createDialog?.entityType === "employee" ? (
        <EmployeeFormDialog
          open
          existing={null}
          onClose={() => setCreateDialog(null)}
          onSaved={(e) => handleCreatedEmployee(createDialog.candidateId, e)}
        />
      ) : null}
      {createDialog?.entityType === "client" ? (
        <ClientFormDialog
          open
          existing={null}
          onClose={() => setCreateDialog(null)}
          onSaved={(c) => handleCreatedClient(createDialog.candidateId, c)}
        />
      ) : null}
    </div>
  );
}

function resolveErrorMessage(t: (key: string) => string, err: unknown): string {
  if (isApiError(err) && err.status === 0) {
    return t("login.errors.network");
  }
  return t("meetings.card.matching.errors.resolveFailed");
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

interface ToolbarProps {
  pendingCount: number;
  canRebuild: boolean;
  rebuildBusy: boolean;
  confirmAllBusy: boolean;
  onRebuild: () => void;
  onConfirmAll: () => void;
}

function MatchingToolbar({
  pendingCount,
  canRebuild,
  rebuildBusy,
  confirmAllBusy,
  onRebuild,
  onConfirmAll,
}: ToolbarProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 border-b border-input pb-2">
      <p
        className="text-sm text-muted-foreground"
        data-testid="matching-pending"
      >
        {pendingCount === 0
          ? t("meetings.card.matching.noPending")
          : `${pendingCount} ${t("meetings.card.matching.pendingCounter")}`}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onConfirmAll}
          disabled={confirmAllBusy}
          data-testid="matching-confirm-all"
          title={t("meetings.card.matching.confirmAllHint")}
        >
          {confirmAllBusy
            ? t("meetings.card.matching.confirmAllBusy")
            : t("meetings.card.matching.confirmAllButton")}
        </Button>
        {canRebuild ? (
          <Button
            size="sm"
            onClick={onRebuild}
            disabled={rebuildBusy}
            data-testid="matching-rebuild"
            title={t("meetings.card.matching.rebuildHint")}
          >
            {rebuildBusy
              ? t("meetings.card.matching.rebuildBusy")
              : t("meetings.card.matching.rebuildButton")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client block
// ---------------------------------------------------------------------------

interface ClientBlockProps {
  candidate: MatchingCandidateClient | null;
  expanded: boolean;
  failedResolveId: UUID | null;
  onToggleExpanded: (id: UUID) => void;
  onLink: (candidateId: UUID, entityId: UUID) => void;
  onSkip: (candidateId: UUID) => void;
  onCreateNew: (candidateId: UUID) => void;
}

function ClientBlock({
  candidate,
  expanded,
  failedResolveId,
  onToggleExpanded,
  onLink,
  onSkip,
  onCreateNew,
}: ClientBlockProps) {
  const { t } = useTranslation();
  return (
    <section data-testid="matching-client-block">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("meetings.card.matching.clientHeader")}
      </h3>
      {candidate === null ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {t("meetings.card.matching.clientEmpty")}
        </p>
      ) : (
        <CandidateRow
          candidate={candidate}
          expanded={expanded}
          resolveFailed={failedResolveId === candidate.id}
          onToggleExpanded={() => onToggleExpanded(candidate.id)}
          onLink={(entityId) => onLink(candidate.id, entityId)}
          onSkip={() => onSkip(candidate.id)}
          onCreateNew={() => onCreateNew(candidate.id)}
          sourceLabel={t("meetings.card.matching.clientFromForm")}
          sourceValue={candidate.source_value}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// «Из диалога» block / mismatch banner — removed (TaskMe 8760a3e1 revisit).
//
// Dmitri simplified the contract: one client per meeting, set manually on
// upload and edited from the card via «Изменить клиента». The transcript-
// extracted candidate list and the «Расхождение» yellow banner are gone
// from the UI; the backend still ships ``client_suggestions_from_text``
// (always [], for back-compat) but no component reads it now.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Speakers block
// ---------------------------------------------------------------------------

interface SpeakersBlockProps {
  speakers: MatchingCandidateSpeaker[];
  expandedRowId: UUID | null;
  failedResolveId: UUID | null;
  onToggleExpanded: (id: UUID) => void;
  onLink: (candidateId: UUID, entityId: UUID) => void;
  onSkip: (candidateId: UUID) => void;
  onCreateNew: (candidateId: UUID) => void;
}

function SpeakersBlock({
  speakers,
  expandedRowId,
  failedResolveId,
  onToggleExpanded,
  onLink,
  onSkip,
  onCreateNew,
}: SpeakersBlockProps) {
  const { t } = useTranslation();
  return (
    <section data-testid="matching-speakers-block">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("meetings.card.matching.speakersHeader")}
      </h3>
      {speakers.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {t("meetings.card.matching.speakersEmpty")}
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {speakers.map((sp) => (
            <li key={sp.id}>
              <CandidateRow
                candidate={sp}
                expanded={expandedRowId === sp.id}
                resolveFailed={failedResolveId === sp.id}
                onToggleExpanded={() => onToggleExpanded(sp.id)}
                onLink={(entityId) => onLink(sp.id, entityId)}
                onSkip={() => onSkip(sp.id)}
                onCreateNew={() => onCreateNew(sp.id)}
                sourceLabel={t("meetings.card.speaker", {
                  label: sp.speaker_label,
                })}
                sourceValue={t("meetings.card.matching.speakerMeta", {
                  count: sp.utterance_count,
                  minutes: Math.max(1, Math.round(sp.total_ms / 60000)),
                })}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Candidate row (shared between client + speakers)
// ---------------------------------------------------------------------------

interface CandidateRowProps {
  candidate: MatchingCandidate;
  expanded: boolean;
  /** Last resolve mutation targeted this row and failed — render inline alert. */
  resolveFailed: boolean;
  onToggleExpanded: () => void;
  onLink: (entityId: UUID) => void;
  onSkip: () => void;
  onCreateNew: () => void;
  sourceLabel: string;
  sourceValue: string;
  /**
   * «Почему этот?» expander state (TaskMe 8760a3e1). When ``whyOpen``
   * is true, the row renders a panel with the top suggestion's
   * ``reason_codes`` translated to human-readable Russian. When the
   * top suggestion has no ``reason_codes[]`` field, the trigger button
   * is hidden entirely so we don't promise an explanation we can't
   * deliver.
   */
  whyOpen?: boolean;
  onToggleWhy?: () => void;
  /**
   * Default true (legacy behaviour): pending rows auto-expand their
   * suggestions list. The «Клиент из диалога» block passes ``false``
   * because every dialog candidate is pending by design and we'd
   * otherwise dump 3 expanded scoreboards on the card. The operator
   * picks via «Изменить» / «Привязать» on the bound line instead.
   */
  autoExpandPending?: boolean;
}

function CandidateRow({
  candidate,
  expanded,
  resolveFailed,
  onToggleExpanded,
  onLink,
  onSkip,
  onCreateNew,
  sourceLabel,
  sourceValue,
  whyOpen = false,
  onToggleWhy,
  autoExpandPending = true,
}: CandidateRowProps) {
  const { t } = useTranslation();
  const status = candidate.status;
  const isBound =
    candidate.bound_entity_id !== null && candidate.bound_display_name !== null;
  const tone = useMemo(() => {
    if (resolveFailed) return "error" as const;
    if (status === "pending") return "warn" as const;
    if (status === "skipped") return "skip" as const;
    return "default" as const;
  }, [status, resolveFailed]);

  return (
    <div
      className={rowClassName(tone)}
      data-testid={`matching-row-${candidate.id}`}
      data-status={status}
      data-resolve-failed={resolveFailed ? "true" : undefined}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium">
          {sourceLabel}
          {sourceValue ? (
            <>
              {" "}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                — {sourceValue}
              </span>
            </>
          ) : null}
        </div>
        <StatusBadge status={status} confirmed={candidate.confirmed} />
      </div>
      {resolveFailed ? (
        <p
          className="mt-1 text-xs text-destructive"
          role="alert"
          data-testid={`matching-row-error-${candidate.id}`}
        >
          {t("meetings.card.matching.errors.resolveFailedInline")}
        </p>
      ) : null}

      {isBound ? (
        <BoundLine
          name={candidate.bound_display_name as string}
          confidence={topConfidence(candidate.suggestions)}
          onChange={onToggleExpanded}
        />
      ) : null}
      {/* When auto-expansion is disabled (e.g. the «Клиент из диалога»
          block), unbound rows have no «Изменить» affordance — we add an
          explicit «Показать варианты» toggle so the operator can still
          reach the scoreboard. */}
      {!isBound && !autoExpandPending && candidate.suggestions.length > 0 ? (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="mt-1 text-xs font-medium text-primary hover:underline"
          data-testid={`matching-show-options-${candidate.id}`}
        >
          {expanded
            ? t("meetings.card.matching.whyHide")
            : t("meetings.card.matching.actionChange")}
        </button>
      ) : null}

      {status === "skipped" && !isBound ? (
        <p className="mt-1 text-xs text-muted-foreground">
          {t("meetings.card.matching.skippedHint")}
        </p>
      ) : null}

      {(expanded || (autoExpandPending && status === "pending" && !isBound)) &&
      candidate.suggestions.length > 0 ? (
        <>
          {status === "pending" && !isBound ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {t("meetings.card.matching.candidatesHint")}
            </p>
          ) : null}
          <ul
            className="mt-2 space-y-1"
            data-testid={`matching-suggestions-${candidate.id}`}
          >
            {candidate.suggestions.slice(0, 3).map((s, i) => (
              <SuggestionItem
                key={s.entity_id}
                suggestion={s}
                isTop={i === 0}
                onLink={() => onLink(s.entity_id)}
              />
            ))}
          </ul>
        </>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCreateNew}
          data-testid={`matching-create-new-${candidate.id}`}
        >
          {candidate.entity_type === "employee"
            ? t("meetings.card.matching.actionCreateNewEmployee")
            : t("meetings.card.matching.actionCreateNewClient")}
        </Button>
        {!isBound && status !== "skipped" ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            data-testid={`matching-skip-${candidate.id}`}
          >
            {t("meetings.card.matching.actionSkip")}
          </Button>
        ) : null}
        {status === "skipped" && candidate.suggestions.length === 0 ? (
          // Skipped without any candidates → manual-link entry point. Currently
          // re-uses "create new" since we have no entity picker yet; expanding
          // the suggestion list also covers the manual flow.
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleExpanded}
            data-testid={`matching-manual-${candidate.id}`}
          >
            {t("meetings.card.matching.actionLinkManual")}
          </Button>
        ) : null}
        {onToggleWhy !== undefined && topReasonCodes(candidate).length > 0 ? (
          // «Почему этот?» — drops the scorer's reason codes for the top
          // suggestion as a human-readable list. Hidden when there is
          // nothing useful to show so we don't promise an explanation
          // the data can't back up (TaskMe 8760a3e1).
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleWhy}
            aria-expanded={whyOpen}
            data-testid={`matching-why-${candidate.id}`}
          >
            {whyOpen
              ? t("meetings.card.matching.whyHide")
              : t("meetings.card.matching.why")}
          </Button>
        ) : null}
      </div>
      {whyOpen && onToggleWhy !== undefined ? (
        <WhyPanel candidate={candidate} />
      ) : null}
    </div>
  );
}

/**
 * Read the ``reason_codes[]`` from the top suggestion of a candidate.
 *
 * Returns an empty array when:
 *   * The candidate has no suggestions (nothing to explain).
 *   * The top suggestion was written before the field was added
 *     (legacy rows without ``reason_codes``).
 *
 * The «Почему этот?» trigger is hidden in both cases so we don't
 * render an empty panel.
 */
function topReasonCodes(candidate: MatchingCandidate): string[] {
  const top = candidate.suggestions[0];
  if (top === undefined) return [];
  return Array.isArray(top.reason_codes) ? top.reason_codes : [];
}

function rowClassName(tone: "default" | "warn" | "skip" | "error"): string {
  const base = "rounded-md border px-3 py-2";
  if (tone === "error") return `${base} border-destructive bg-destructive/5`;
  if (tone === "warn") return `${base} border-amber-300 bg-amber-50/60`;
  if (tone === "skip")
    return `${base} border-input bg-muted/30 text-muted-foreground`;
  return `${base} border-input bg-card`;
}

function topConfidence(suggestions: MatchingSuggestion[]): number | null {
  if (suggestions.length === 0) return null;
  const s = suggestions[0];
  return s === undefined ? null : s.confidence;
}

// ---------------------------------------------------------------------------
// Bound line: "→ Иван Петров [confidence bar] [Изменить]"
// ---------------------------------------------------------------------------

interface BoundLineProps {
  name: string;
  confidence: number | null;
  onChange: () => void;
}

function BoundLine({ name, confidence, onChange }: BoundLineProps) {
  const { t } = useTranslation();
  return (
    <div className="mt-1 flex items-center justify-between gap-2 rounded border border-input bg-background px-2 py-1">
      <div className="text-sm">
        <span className="mr-1 text-muted-foreground">
          {t("meetings.card.matching.boundTo")}
        </span>
        <strong>{name}</strong>
      </div>
      <div className="flex min-w-[180px] items-center gap-2">
        {confidence !== null ? <ConfidenceBar value={confidence} /> : null}
        <Button variant="outline" size="sm" onClick={onChange}>
          {t("meetings.card.matching.actionChange")}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggestion item — confidence bar + "Привязать" CTA
// ---------------------------------------------------------------------------

interface SuggestionItemProps {
  suggestion: MatchingSuggestion;
  isTop: boolean;
  onLink: () => void;
}

function SuggestionItem({ suggestion, isTop, onLink }: SuggestionItemProps) {
  const { t } = useTranslation();
  return (
    <li
      className="flex items-center gap-2 rounded border border-input bg-background px-2 py-1"
      data-testid={`matching-candidate-${suggestion.entity_id}`}
    >
      <div className={`flex-none text-sm ${isTop ? "font-semibold" : ""}`}>
        {suggestion.display_name}
      </div>
      <div className="flex flex-1 items-center gap-2">
        <ConfidenceBar value={suggestion.confidence} />
      </div>
      <Button
        size="sm"
        variant={isTop ? "default" : "outline"}
        onClick={onLink}
        data-testid={`matching-link-${suggestion.entity_id}`}
      >
        {t("meetings.card.matching.actionLink")}
      </Button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({
  status,
  confirmed,
}: {
  status: MatchingCandidate["status"];
  confirmed: boolean;
}) {
  const { t } = useTranslation();
  const cls = badgeClass(status);
  const label =
    status === "manual" && confirmed
      ? t("meetings.card.matching.status.confirmed")
      : t(`meetings.card.matching.status.${status}`);
  return (
    <span
      className={cls}
      data-testid="matching-status-badge"
      data-status={status}
    >
      {label}
    </span>
  );
}

function badgeClass(status: MatchingCandidate["status"]): string {
  const base =
    "inline-block rounded-full border px-2 py-[1px] text-[11px] font-medium";
  if (status === "auto" || status === "manual") {
    return `${base} border-emerald-300 bg-emerald-50 text-emerald-700`;
  }
  if (status === "pending") {
    return `${base} border-amber-300 bg-amber-50 text-amber-700`;
  }
  return `${base} border-input bg-muted text-muted-foreground`;
}

// ---------------------------------------------------------------------------
// Confidence bar (presentational)
// ---------------------------------------------------------------------------

export function ConfidenceBar({ value }: { value: number }) {
  const { t } = useTranslation();
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const tone = value >= 0.7 ? "ok" : value >= 0.4 ? "mid" : "low";
  const bg =
    tone === "ok"
      ? "bg-emerald-500"
      : tone === "mid"
        ? "bg-amber-500"
        : "bg-slate-300";
  return (
    <div
      className="flex flex-1 items-center gap-2"
      title={t("meetings.card.matching.confidenceTooltip", {
        value: value.toFixed(2),
      })}
    >
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div className={`h-full ${bg}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 text-right font-mono text-[11px] text-muted-foreground">
        {value.toFixed(2)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// «Почему этот?» panel — translates scorer reason codes to Russian
// ---------------------------------------------------------------------------

interface WhyPanelProps {
  candidate: MatchingCandidate;
}

/**
 * Render the top suggestion's ``reason_codes`` as a human-readable list.
 *
 * Each raw code (`exact_alias`, `legal_form_match`, etc.) is looked up
 * in the `matching.reasonCodes.*` i18n dictionary. Codes the dictionary
 * doesn't know are stripped of their `_NUMBER` suffix (the scorer emits
 * variants like `fuzzy_name_0.92` per match) and retried; the bare
 * variant carries the human label. As a last-line fallback we humanize
 * the code (`underscores → spaces`, capitalize) so the operator never
 * sees a snake_case token verbatim — the QA test pins this property.
 */
function WhyPanel({ candidate }: WhyPanelProps) {
  const { t } = useTranslation();
  const codes = topReasonCodes(candidate);
  const labels = codes
    .map((code) => translateReasonCode(t, code))
    .filter((label) => label.length > 0);
  return (
    <div
      className="mt-2 rounded border border-input bg-muted/30 px-2 py-1 text-xs text-muted-foreground"
      data-testid={`matching-why-panel-${candidate.id}`}
    >
      {labels.length === 0 ? (
        <span>{t("meetings.card.matching.whyEmpty")}</span>
      ) : (
        <ul className="list-disc space-y-0.5 pl-4">
          {labels.map((label, i) => (
            <li key={`${candidate.id}-why-${i}`}>{label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Translate one scorer reason code into a Russian human-readable label.
 *
 * Falls back through three lookup stages so an unknown code never
 * leaks through verbatim:
 *   1. Exact key match (`matching.reasonCodes.exact_alias`).
 *   2. Bare key after stripping a trailing `_NUMBER` (`fuzzy_name_0.92`
 *      → `fuzzy_name`).
 *   3. Humanization fallback — replace underscores with spaces and
 *      capitalize. Still readable Russian-ish prose even for codes
 *      the dictionary hasn't caught up to yet.
 */
function translateReasonCode(
  t: (key: string, opts?: Record<string, unknown>) => string,
  code: string,
): string {
  const exactKey = `meetings.card.matching.reasonCodes.${code}`;
  const exact = t(exactKey);
  if (exact !== exactKey) return exact;
  // Strip a trailing `_<digits-or-dot>` so `fuzzy_name_0.92` → `fuzzy_name`.
  const bare = code.replace(/_[0-9.]+$/, "");
  if (bare !== code) {
    const bareKey = `meetings.card.matching.reasonCodes.${bare}`;
    const bareLabel = t(bareKey);
    if (bareLabel !== bareKey) return bareLabel;
  }
  // Fallback: humanize the snake_case token.
  const humanized = code.replace(/_/g, " ").trim();
  if (humanized.length === 0) return "";
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}
